import { EnergyLabel } from "@omni/components/components/Product/energyLabel/energyLabel";
import { useProductPageContext } from "@omni/components/components/ProductPage/context/productPage.context";
import { ProductPage as BaseProductPage } from "@omni/components/components/ProductPage/productPage";
import { useFeatureToggle } from "@omni/components/context/featureToggles.context";
import { useGranite } from "@omni/components/redux/modules/value/translations/translations.provider";
import { DamImage } from "@omni/components/utils/dam.utils";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { GridContainer } from "@omni/ui/components/Grid/gridContainer";
import { cn } from "@omni/utils/styles";

import { ECommerceContainer } from "@/components/Product/ECommerce/eCommerce.container";

export const ProductPageComponent = () => {
    const { t } = useGranite();

    const {
        product,
        infoCards,
        infoLink,
        tooltips,
        energyLabel,
        cashbackPagePath,
        contextAwareXfPath,
        paintConfiguratorPath,
        isEcochequesAccepted,
        isClickAndCollectAvailable,
        isExclusivelyOnline,
        isWebOnly
    } = useProductPageContext();

    const isSticky = useFeatureToggle(
        FeatureToggleKey.FEATURE_STICKY_ECOMMERCE
    );

    const isReviewsEnabled = useFeatureToggle(FeatureToggleKey.FEATURE_REVIEWS);

    return (
        <>
            <BaseProductPage.Title>
                <BaseProductPage.Title.RatingSummary />
            </BaseProductPage.Title>
            <GridContainer>
                <div
                    className={cn(
                        "tw:md:row-start-1 tw:md:col-start-1 tw:row-end-2 tw:col-end-2"
                    )}
                >
                    <BaseProductPage.ImageViewer />
                </div>
                <div
                    className={cn(
                        "tw:hidden tw:md:block",
                        "tw:md:row-start-2 tw:md:col-start-1 tw:row-end-3 tw:col-end-2",
                        "tw:lg:row-start-1 tw:lg:col-start-2 tw:lg:row-end-2 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.ProductFacing
                        className={cn("tw:hidden", "tw:lg:flex")}
                    />

                    <BaseProductPage.Promotions
                        className={cn("tw:hidden tw:md:block")}
                    />

                    <div
                        className={cn(
                            "tw:flex tw:items-center tw:justify-between tw:flex-wrap"
                        )}
                    >
                        {product.brand && (
                            <div>
                                <BaseProductPage.BrandLogo />
                            </div>
                        )}
                        <EnergyLabel
                            energyLabel={energyLabel}
                            className={cn("tw:flex tw:w-fit tw:justify-end")}
                        >
                            <EnergyLabel.LabelNested />
                            <EnergyLabel.Fiche />
                        </EnergyLabel>
                    </div>

                    <div className={cn("tw:hidden tw:lg:block")}>
                        <BaseProductPage.KeyFeatures />
                    </div>

                    <BaseProductPage.PromoBanner />
                </div>

                <div
                    className={cn(
                        {
                            "tw:sticky tw:top-6": isSticky
                        },
                        "tw:row-start-1 tw:col-start-2 tw:row-end-[span_5] tw:col-end-3",
                        "tw:lg:row-start-1 tw:lg:col-start-3 tw:lg:row-end-[span_5] tw:lg:col-end-4"
                    )}
                >
                    <BaseProductPage.RecoverabilityIndex />

                    <EnergyLabel
                        energyLabel={energyLabel}
                        className={cn(
                            "tw:flex tw:md:hidden tw:w-fit tw:justify-start tw:mb-4"
                        )}
                    >
                        <EnergyLabel.LabelNested />
                        <EnergyLabel.Fiche />
                    </EnergyLabel>

                    <BaseProductPage.Promotions
                        className={cn("tw:block tw:md:hidden")}
                    />

                    <ECommerceContainer
                        product={product}
                        infoLink={infoLink || ""}
                        tooltips={tooltips}
                        cashbackPagePath={cashbackPagePath}
                        contextAwareXfPath={contextAwareXfPath}
                        paintConfiguratorPath={paintConfiguratorPath}
                    />

                    <BaseProductPage.PromoBanner
                        className={cn("tw:block tw:md:hidden")}
                    />

                    <div className={cn("tw:hidden tw:md:block")}>
                        {isEcochequesAccepted && (
                            <div>
                                <BaseProductPage.InfoCard
                                    title={t("infoCard.ecoCheques.title")}
                                    icon={DamImage.icon.label.ecoCheques}
                                    iconAltText={t(
                                        "infoCard.ecoCheques.iconAltText"
                                    )}
                                    description={t(
                                        "infoCard.ecoCheques.description"
                                    )}
                                    link={infoCards.ecoChequeLink}
                                    linkLabel={t(
                                        "infoCard.ecoCheques.linkLabel"
                                    )}
                                />
                            </div>
                        )}
                        {isClickAndCollectAvailable && (
                            <div>
                                <BaseProductPage.InfoCard
                                    title={t("infoCard.transport.title")}
                                    icon={DamImage.icon.label.transport}
                                    iconAltText={t(
                                        "infoCard.transport.iconAltText"
                                    )}
                                    description={t(
                                        "infoCard.transport.description"
                                    )}
                                    link={infoCards.transportLink}
                                    linkLabel={t(
                                        "infoCard.transport.linkLabel"
                                    )}
                                />
                            </div>
                        )}
                        {isExclusivelyOnline && (
                            <div>
                                <BaseProductPage.InfoCard
                                    title={t(
                                        "infoCard.exclusivelyOnline.title"
                                    )}
                                    icon={DamImage.icon.label.homeDelivery}
                                    iconAltText={t(
                                        "infoCard.exclusivelyOnline.iconAltText"
                                    )}
                                    description={t(
                                        "infoCard.exclusivelyOnline.description"
                                    )}
                                    link={infoCards.homeDeliveryLink}
                                    linkLabel={t(
                                        "infoCard.exclusivelyOnline.linkLabel"
                                    )}
                                />
                            </div>
                        )}
                        {isWebOnly && (
                            <div>
                                <BaseProductPage.InfoCard
                                    title={t("infoCard.webOnly.title")}
                                    icon={DamImage.icon.label.webOnly}
                                    iconAltText={t(
                                        "infoCard.webOnly.iconAltText"
                                    )}
                                    description={t(
                                        "infoCard.webOnly.description"
                                    )}
                                    link={infoCards.webOnlyLink}
                                    linkLabel={t("infoCard.webOnly.linkLabel")}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className={cn(
                        "tw:md:row-start-3 tw:md:col-start-1 tw:row-end-4 tw:col-end-2",
                        "tw:lg:row-start-4 tw:lg:col-start-1 tw:lg:row-end-5 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.ProductBundles />
                    <BaseProductPage.ProductInfo />
                </div>

                <div
                    className={cn(
                        "tw:md:-mt-3.75",
                        "tw:md:row-start-4 tw:md:col-start-1 tw:md:row-end-5 tw:md:col-end-2",
                        "tw:lg:row-start-2 tw:lg:col-start-1 tw:lg:row-end-3 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.RelatedProductsSlider
                        productSource={ProductSliderSources.Accessory}
                    />

                    <BaseProductPage.RelatedProductsSlider
                        productSource={ProductSliderSources.MainProduct}
                    />
                </div>

                {isReviewsEnabled && (
                    <div
                        className={cn(
                            "tw:md:row-start-5 tw:md:col-start-1 tw:md:row-end-6 tw:md:col-end-3",
                            "tw:lg:row-start-5 tw:lg:col-start-1 tw:lg:row-end-6 tw:lg:col-end-4"
                        )}
                    >
                        <BaseProductPage.Review />
                    </div>
                )}

                <div
                    className={cn(
                        "tw:block tw:md:hidden",
                        "tw:md:row-start-2 tw:md:col-start-2 tw:md:row-end-3 tw:md:col-end-3",
                        "tw:lg:row-start-2 tw:lg:col-start-3 tw:lg:row-end-3 tw:lg:col-end-4"
                    )}
                >
                    {isEcochequesAccepted && (
                        <div>
                            <BaseProductPage.InfoCard
                                title={t("infoCard.ecoCheques.title")}
                                icon={DamImage.icon.label.ecoCheques}
                                iconAltText={t(
                                    "infoCard.ecoCheques.iconAltText"
                                )}
                                description={t(
                                    "infoCard.ecoCheques.description"
                                )}
                                link={infoCards.ecoChequeLink}
                                linkLabel={t("infoCard.ecoCheques.linkLabel")}
                            />
                        </div>
                    )}
                    {isClickAndCollectAvailable && (
                        <div>
                            <BaseProductPage.InfoCard
                                title={t("infoCard.transport.title")}
                                icon={DamImage.icon.label.transport}
                                iconAltText={t(
                                    "infoCard.transport.iconAltText"
                                )}
                                description={t(
                                    "infoCard.transport.description"
                                )}
                                link={infoCards.transportLink}
                                linkLabel={t("infoCard.transport.linkLabel")}
                            />
                        </div>
                    )}
                    {isExclusivelyOnline && (
                        <div>
                            <BaseProductPage.InfoCard
                                title={t("infoCard.exclusivelyOnline.title")}
                                icon={DamImage.icon.label.homeDelivery}
                                iconAltText={t(
                                    "infoCard.exclusivelyOnline.iconAltText"
                                )}
                                description={t(
                                    "infoCard.exclusivelyOnline.description"
                                )}
                                link={infoCards.homeDeliveryLink}
                                linkLabel={t(
                                    "infoCard.exclusivelyOnline.linkLabel"
                                )}
                            />
                        </div>
                    )}
                    {isWebOnly && (
                        <div>
                            <BaseProductPage.InfoCard
                                title={t("infoCard.webOnly.title")}
                                icon={DamImage.icon.label.webOnly}
                                iconAltText={t("infoCard.webOnly.iconAltText")}
                                description={t("infoCard.webOnly.description")}
                                link={infoCards.webOnlyLink}
                                linkLabel={t("infoCard.webOnly.linkLabel")}
                            />
                        </div>
                    )}
                </div>

                <div
                    className={cn(
                        "tw:md:row-start-6 tw:md:col-start-1 tw:row-end-7 tw:col-end-2",
                        "tw:lg:row-start-3 tw:lg:col-start-1 tw:lg:row-end-4 tw:lg:col-end-3"
                    )}
                >
                    <BaseProductPage.RecommendProductsSlider />
                </div>
            </GridContainer>

            <div>
                <BaseProductPage.RecentlyViewedProductSlider />
            </div>
        </>
    );
};
